<script lang="ts">
  import {SearchState} from "./services/search-service";
  import {cartEmail} from "./services/cart-service";
  import {deleteAllSearchQueryStringParameter, setQueryStringParameter} from "./url";
  import type {OrderObject, OrderStatusResponse} from "./models/order.interface";
  import {createForm} from "svelte-forms-lib";
  import * as yup from "yup";
  import Pageination from "./components/Pageination.svelte";
  import moment from "moment";
  import ErrorComponent from "./components/ErrorComponent.svelte";
  import TooltipHybridButton from "./components/search/TooltipHybridButton.svelte";

  // TODO: We probably want interfaces for these at some point -AB
  let orderStatusLoaded: Promise<OrderStatusResponse>;

  const PAGE_SIZE = 10;
  const orderEndpoint = new URL(process.env.ORDER_URL);

  const certifiablePath = new URL(process.env.CERTIFY_URL);
  const datasetToProductMap = new Map([
    ["daily-summaries", "GHCND"],
    ["global-hourly", "LCD-ISD"],
    ["global-marine", "MARINE"],
    ["global-summary-of-the-month", "GSOM"],
    ["global-summary-of-the-year", "GSOY"],
    ["normals-annualseasonal", "NORMAL_ANN"],
    ["normals-daily", "NORMAL_DLY"],
    ["normals-hourly", "NORMAL_HLY"],
    ["normals-monthly", "NORMAL_MLY"],
    ["normals-hourly-2006-2020", "NORMAL_HLY"],
    ["normals-hourly-1991-2020", "NORMAL_HLY"],
    ["normals-daily-2006-2020", "NORMAL_DLY"],
    ["normals-daily-1991-2020", "NORMAL_DLY"],
    ["normals-monthly-2006-2020", "NORMAL_MLY"],
    ["normals-monthly-1991-2020", "NORMAL_MLY"],
    ["normals-annualseasonal-2006-2020", "NORMAL_ANN"],
    ["normals-annualseasonal-1991-2020", "NORMAL_ANN"],
    ["local-climatological-data", "LCD"],
    ["local-climatological-data-publication", "LCD_FORM"],
    ["local-climatological-data-v2", "LCD"],
    ["cooperative-weather-observations-publication", "cooperative-weather-observations-publication"]
  ]);

  // Format option labels using a dictionary approach with fallback
  function formatToReadableLabel(text: string): string {
    if (!text) return "";

    // Dictionary for common terms - add more as needed
    const dictionary = {
      enddate: "END DATE",
      startdate: "START DATE",
      includedocumentation: "INCLUDE DOCUMENTATION",
      filename: "FILE NAME",
      filetype: "FILE TYPE",
      datatypes: "DATA TYPES",
      includeattributes: "INCLUDE ATTRIBUTES",
      includestationlocation: "INCLUDE STATION LOCATION",
      includestationname: "INCLUDE STATION NAME"
    };

    // Look up in dictionary (case insensitive)
    const key = text.toLowerCase();
    if (dictionary[key]) {
      return dictionary[key];
    }

    // Fallback: Handle camelCase and return uppercase
    return text.replace(/([a-z])([A-Z])/g, "$1 $2");
  }

  SearchState.subscribe((search) => {
    search.params.set("limit", PAGE_SIZE.toString());
    search.params.set("offset", ((search.pageNum - 1) * PAGE_SIZE).toString());

    if (search.params.has("email")) {
      const orderSearchParams = new URLSearchParams();

      Array.from([...search.params.entries()]).flatMap(([key, value]) => {
        orderSearchParams.append(key === 'orderId' ? 'id' : key, value);
      });

      orderStatusLoaded = fetchOrderStatus(orderSearchParams);
    }
  });

  // may have to add these as headers?
  // -H 'accept: application/json'
  // -H 'Content-Type: application/json'
  async function fetchOrderStatus(params: URLSearchParams) {
    /*
    https://d1gyq09y3ut5oh.cloudfront.net - cloudfront
    https://bonsoma618.execute-api.us-east-1.amazonaws.com - api gateway
    https://ob1.data-dev.nesdis.noaa.gov - literally just a cloudfront alias

    original fetch to v1 for order status
    fetch(`//www.ncei.noaa.gov/access/services/orders/v1?${params}`)
    test a GET
    fetch(`https://ob1.data-dev.nesdis.noaa.gov/api/v2/get-order-status?${params}`)
    test a POST (force a CORS preflight)
    fetch('https://ob1.data-dev.nesdis.noaa.gov/api/v2/',{method:'POST',headers:{'Content-Type':'application/json'}})
     */
    //process.env.OB1_STATUS_UPDATE_URL is coming from the top level .env.defaults file, loaded into the application
    // by the rollup.config.js
    // promise = fetch(`${process.env.OB1_BASE_URL}/api/v2/get-order-status?${params}`)

    // this fetch url should be changed when OB1 and Salesforce are put into play
    // promise = fetch(`${process.env.ORDER_URL}?${params}`)

    const response = await fetch(`${orderEndpoint}/v1?${params}`).catch((err) => {
      console.error("Network problem =>", err.message);
      throw err;
    });

    if (!response.ok) {
      const errBody = await response.json()
      const err = new Error(`Failed to fetch order status. message: ${errBody.message || "Unknown"}`);
      console.error(err.message);
      throw err;
    }

    return await response.json();
  }

  // Link to certify order
  function certifyOrder(o: OrderObject): string {
    // Convert ProductId to abbreviated version for URL
    const productId = datasetToProductMap.get(o.datasetId);
    if (!productId) {
      console.error(`Product ID not found for datasetId: ${o.datasetId}`);
      return;
    }
    return o.orderStatus ? `${certifiablePath}cdo2nes/proxy.cmd?productid=${productId}&orderid=${o.orderStatus.id}`:
            `${certifiablePath}cdo2nes/proxy.cmd?productid=${productId}&orderid=${o.orderStatus}`;

  }

  // checks to see if the order is over 7 days old
  function validCertify(orderDate: string): boolean {
    const date = new Date(orderDate);
    const currentDate = moment().format();
    const cutOffDate = moment(date).add(7, "d").format();

    return currentDate < cutOffDate;
  }

  // sets up the email and order id form at the top of the page
  const { form, touched, errors, state, handleChange, handleSubmit, isSubmitting } = createForm({
    initialValues: {
      email: $SearchState.params.get("email") || $cartEmail,
      orderId: $SearchState.params.get("orderId")
    },
    validationSchema: yup
      .object()
      .shape({
        email: yup
          .string()
          .email("Please enter a valid email address in the format: name@example.gov")
          .lowercase(),
        orderId: yup
          .string()
          // .matches(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, "must be a valid Order Id")
          .matches(/[0-9]*/, "Please enter a valid Order ID")
          .nullable()
      })
      .strict(true),
    onSubmit: () => {
      // add this offset param to the ob1 api
      setQueryStringParameter("offset", "0");
      if ($form.email) {
        setQueryStringParameter("email", $form.email);
      }

      if ($form.orderId) {
        setQueryStringParameter("orderId", $form.orderId);
      } else {
        deleteAllSearchQueryStringParameter("orderId");
      }
    }
  });
</script>

<div class="container-fluid d-flex flex-column min-vh-100">
  <div class="row justify-content-center">
    <div class="col-md-6 text-center">
      <h2 class="py-3">Check Order Status</h2>
      <p class="lead">
        To retrieve all orders, enter your email address. To retrieve a specific order, enter both
        your email address and order ID.
      </p>
      <!-- Email & Order Id Form -->
      <form on:submit={handleSubmit}>
        <div class="mb-3">
          <label id="emailLabel" for="emailInput" class="form-label"
          >Email <span class="text-danger">*</span>
          </label>
          <TooltipHybridButton
            searchUrlQueryParam="email"
            tooltip="Enter the email address used when placing your order. Example: user@example.gov"
          />
          <input
            id="emailInput"
            name="email"
            class="form-control"
            placeholder="Email Address"
            aria-describedby="emailToolTipContent {$errors.email ? 'email-error' : ''}"
            aria-required="true"
            on:change={handleChange}
            on:blur={handleChange}
            bind:value={$form.email}
            on:input={() => $form.email = $form.email.toLowerCase()}
          />
          {#if $errors.email}
            <div id="email-error" class="error-message mt-1" role="alert">
              <i class="bi bi-exclamation-triangle-fill text-danger" aria-hidden="true"></i>
              <small class="text-danger">{$errors.email}</small>
            </div>
          {/if}
        </div>
        <div class="mb-3">
          <label for="orderIdInput" class="form-label"
          >Order ID <span class="text-muted">(Optional)</span>
          </label>
          <TooltipHybridButton
            searchUrlQueryParam="orderId"
            tooltip="Optional. Add this to find a specific order instead of all orders for your email
            address."
          />
          <input
            id="orderIdInput"
            name="orderId"
            class="form-control"
            placeholder="Order ID"
            aria-describedby="orderIdToolTipContent {$errors.orderId ? 'orderId-error' : ''}"
            on:change={handleChange}
            on:blur={handleChange}
            bind:value={$form.orderId}
          />
          {#if $errors.orderId}
            <div id="orderId-error" class="error-message mt-1" role="alert">
              <i class="bi bi-exclamation-triangle-fill text-danger" aria-hidden="true"></i>
              <small class="text-danger">{$errors.orderId}</small>
            </div>
          {/if}
        </div>
        {#if $form.email === "" && $form.orderId === ""}
          <button type="submit" class="btn ae-secondary" autocapitalize="off" disabled={true}>
            <i class="bi bi-check" />
            Submit
          </button>
        {:else if $form.email === ""}
          <button type="submit" class="btn ae-secondary" autocapitalize="off" disabled={true}>
            <i class="bi bi-check" />
            Submit
          </button>
        {:else}
          <button type="submit" class="btn ae-primary" autocapitalize="off">
            <i class="bi bi-check" />
            Submit
          </button>
        {/if}
      </form>
    </div>
  </div>
  <div class="my-5" />
  {#await orderStatusLoaded}
    <div class="d-flex justify-content-center">
      <div class="spinner-border text-secondary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
  {:then osr}
    <!-- Order Cards -->
    <div class="row row-cols-lg-3 row-cols-md-2 row-cols-sm-1">
      {#if osr?.count === 0}
        <div class="col mb-2">
          <div class="card">
            <div class="card-body">
              <h5 class="card-title">No Orders Found</h5>
            </div>
          </div>
        </div>
      {/if}
      {#each osr?.orders || [] as o, i}
        {#if o.orderStatus}
          <div class="col mb-2">
            <div class="card h-100">
              <div class="card-body">
              <h4>
                {o.datasetId
                  .split("-")
                  .map((word) => {
                    return word.charAt(0).toUpperCase().concat(word.slice(1));
                  })
                  .join(" ")}
              </h4>
              <h5 class="card-title">
                <span style="white-space: pre-line">
                  Order ID: {o.orderStatus.id}
                </span>
              </h5>
              <div style="overflow-x: auto;">
                <table class="table table-borderless" style="width: 100%; table-layout: fixed;">
                  <tbody>
                  <tr>
                    <td>Status</td>
                    <td>{o.orderStatus.status}</td>
                  </tr>
                  <tr>
                    <td>Submitted</td>
                    <td>{o.orderStatus.submitted}</td>
                  </tr>
                  <tr>
                    <td>Completed</td>
                    <td>{!!o.orderStatus.completed}</td>
                  </tr>
                  <tr>
                    <td>Format</td>
                    <td>{o.format}</td>
                  </tr>
                  {#each o.orderOptions as options}
                    {#if options.values.length !== 0}
                      <tr class="">
                        <td>{formatToReadableLabel(options.name || options.id)}</td>
                        <td>
                          {#if options.values.length === 1}
                            {#if options.values[0] !== null && typeof options.values[0] === "object" && options.values[0].value}
                              {options.values[0].value}
                            {:else}
                              {options.values[0]}
                            {/if}
                          {:else}
                            {options.values
                                    .map((val) =>
                                            typeof val === "object" && val.value ? val.value : val
                                    )
                                    .join(", ")}
                          {/if}
                        </td>
                      </tr>
                    {/if}
                  {/each}
                  <tr>
                    <td>Eligible for Certification</td>
                    <td>
                      {#if !!o.orderStatus.completed}
                        {o.orderStatus.certifiable}
                      {/if}
                    </td>
                  </tr>
                  </tbody>
                </table>
              </div>
              </div>
              <div class="card-footer">
                <div class="d-flex justify-content-end">
                {#if o.orderStatus.certifiable.valueOf() === true && validCertify(o.orderStatus.submitted) === true && o.orderStatus.completed}
                  <a role="button" class="btn ae-tertiary mx-2" href={certifyOrder(o)}>
                    <i class="bi bi-award-fill" />
                    Certify
                  </a>
                {:else if o.orderStatus.certifiable.valueOf() === true && !validCertify(o.orderStatus.submitted) === true}
                  <span id="disabledCertify" class="d-inline-block">
                    <button
                            type="button"
                            class="btn ae-tertiary disabled mx-2"
                            aria-label="Disabled Certify Tool Tip"
                            aria-describedby="disabledCertifyToolTipContent"
                    >
                      <i class="bi bi-award-fill" />
                      Certify
                    </button>
                  </span>
                    <TooltipHybridButton
                            searchUrlQueryParam="disabled-certify-{o.orderStatus.id}"
                            tooltip="#disable-certify-tooltip"
                    />
                    <!--                  <Tooltip-->
                    <!--                    id="disabledCertifyToolTipContent"-->
                    <!--                    target={`disabledCertify`}-->
                    <!--                    placement="top"-->
                    <!--                    role="tooltip"-->
                    <!--                  >-->
                    <!--                    <p>-->
                    <!--                      Orders are only certifiable up to <b> 7 days after completion. </b> If this time-->
                    <!--                      frame has passed you will need to reorder the data to start the certification process.-->
                    <!--                    </p>-->
                    <!--                  </Tooltip>-->
                  {/if}
                  {#if o.orderStatus.downloadURL !== null && o.orderStatus.completed}
                    <a
                            class="btn ae-tertiary mx-2"
                            href={o.orderStatus.downloadURL}
                            target="_blank"
                            rel="noopener noreferrer"
                    >
                      <i class="bi bi-download" /> Download
                    </a>
                  {:else}
                  <span id="disabledDownload" class="d-inline-block">
                    <button
                            class="btn ae-tertiary disabled mx-2"
                            aria-label="Disabled Download Tool Tip"
                            aria-describedby="disabledDownloadToolTipContent"
                    >
                      <i class="bi bi-download" />
                      Download
                    </button>
                  </span>
                    <TooltipHybridButton
                            searchUrlQueryParam="disabled-download-{o.orderStatus.id}"
                            tooltip="#disable-download-tooltip"
                    />
                    <!--                  <Tooltip-->
                    <!--                    id="disabledDownloadToolTipContent"-->
                    <!--                    target={`disabledDownload`}-->
                    <!--                    placement="top"-->
                    <!--                    role="tooltip"-->
                    <!--                  >-->
                    <!--                    <p>-->
                    <!--                      Orders can be downloaded up to <b> 7 days after completion. </b> If this time frame-->
                    <!--                      has expired you need to reorder the data to download it again.-->
                    <!--                    </p>-->
                    <!--                  </Tooltip>-->
                  {/if}
                </div>
              </div>
            </div>
          </div>
        {/if}
      {/each}
    </div>
    {#if osr?.count > 1}
      <div class="row justify-content-center">
        <div class="col">
          <Pageination pageSize={PAGE_SIZE} count={osr?.count} />
        </div>
      </div>
    {/if}
  {:catch error}
    <ErrorComponent {error} />
  {/await}
</div>

<style>
    #error-container {
        height: 45vh;
    }

    #error-card {
        display: flex;
        width: 45%;
        background-color: #e7b1b6;
        margin: 3vh auto;
        border-radius: 2vh;
        padding: 2vh;
    }

    #error-container #error-card .custom-error-message {
        font-size: clamp(1rem, 2vw, 1.5rem);
        margin: auto;
        text-align: center;
    }

    .form-control:focus {
        border-color: #0066cc !important;
        box-shadow: 0 0 0 0.2rem rgba(0, 102, 204, 0.5) !important;
        outline: 2px solid #0066cc !important;
        outline-offset: 2px;
    }

    .btn:focus,
    .btn:focus-visible {
        box-shadow: 0 0 0 0.2rem rgba(0, 102, 204, 0.5) !important;
        outline: 2px solid #0066cc !important;
        outline-offset: 2px;
    }

    .error-message {
        display: flex;
        align-items: center;
        gap: 0.25rem;
    }
</style>
